/**
 * Client-side gate deciding whether to surface the cross-site
 * "reunification started" Join prompt.
 *
 * The backend remains the source of truth — every Pause/Resume/Complete
 * call re-checks the permission and returns 403 if the user can't manage
 * the event. This helper only decides whether to *show* the prompt.
 *
 * Gate:
 *   1. Vendor users are multi-tenant by definition → always allowed.
 *   2. Otherwise the user must (a) carry the
 *      `reunification.activate_event` global permission and (b) be a
 *      member of the school the reunification was activated on.
 */

import type { UserSchoolMembership } from '../services/schools.service';

export const MANAGE_REUNIFICATION_PERMISSION = 'reunification.activate_event';

export interface ManageGateInput {
  permissions: readonly string[];
  memberships: readonly UserSchoolMembership[] | undefined;
  isVendorUser: boolean;
  schoolId: number | null | undefined;
}

export function canManageReunificationFor({
  permissions,
  memberships,
  isVendorUser,
  schoolId,
}: ManageGateInput): boolean {
  if (schoolId == null) return false;
  if (isVendorUser) return true;
  if (!permissions.includes(MANAGE_REUNIFICATION_PERMISSION)) return false;
  if (!memberships || memberships.length === 0) return false;
  return memberships.some((m) => m.school === schoolId);
}
